import React, { useEffect, useState } from 'react';
import { addLogComment, getLogsByID, addLog } from '../services/api/implementation/impl';
import { useQuery } from '@tanstack/react-query';
import LogCard from '../components/layout/LogCard';
import NoDataFoundImg from '../../shared/assets/png/noDataFound.png';
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { Plus, X, ScrollText } from 'lucide-react';
import Button from '../../shared/components/ui/Button';
import Modal from '../../shared/components/ui/Modal';
import axios from 'axios';

const Logs = ({ salesId, variant = 'default' }) => {
  const userId = Cookies.get('userId');
  const userVertical = String(Cookies.get('userVertical') || '').toLowerCase();
  const canAddStandaloneNote =
    userVertical === 'sales' || userVertical === 'management';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddLogModal, setShowAddLogModal] = useState(false);
  const [logDescription, setLogDescription] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const {
    data: activityId,
    refetch: refetchLeadbyId,
    isFetching
  } = useQuery({
    queryKey: ['getLogsByID', salesId],
    queryFn: () => getLogsByID(salesId),
    enabled: !!salesId
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Logs Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    refetchLeadbyId();
  }, [salesId]);

  const handleCommentSubmit = async (logId, commentText) => {
    if (!commentText.trim()) return;

    setIsSubmitting(true);
    const payload = {
      comment: commentText,
      log_id: Number(logId)
    };

    try {
      const res = await addLogComment(payload);

      await refetchLeadbyId({ cancelRefetch: false });
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const token = Cookies.get('token');
      if (!token) throw new Error('No token found');

      const formData = new FormData();
      formData.append('files', file);

      const res = await axios.post('https://application.housingram.com/api/s3/upload', formData, {
        headers: {
          authorization: token,
          'Content-Type': 'multipart/form-data'
        }
      });

      const uploaded = res.data.data.files[0];
      setUploadedFile({
        name: uploaded.originalName,
        url: uploaded.presignedUrl
      });
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleAddLog = async () => {
    if (!logDescription) {
      toast.error('Please enter log description');
      return;
    }

    const payload = {
      name: logDescription,
      description: logDescription,
      sales_user_id: Number(userId),
      lead_id: salesId,
      file: uploadedFile?.url || ''
    };

    console.log('PAYLOAD', JSON.stringify(payload));

    try {
      setIsSubmitting(true);
      await addLog(payload);
      toast.success('Log added successfully');
      setShowAddLogModal(false);
      setLogDescription('');
      setUploadedFile(null);
      await refetchLeadbyId();
    } catch (err) {
      console.error('Error in add log:', err);
      toast.error(err?.response?.data?.message || 'Error in add log');
    } finally {
      setIsSubmitting(false);
    }
  };

  const rootClassName =
    variant === 'split'
      ? 'mt-2 w-full min-w-0 overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-900/[0.04]'
      : 'mx-auto mt-2 w-full overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-900/[0.04]';

  return (
    <div className={rootClassName}>
      <div className="border-b border-slate-100/80 bg-gradient-to-r from-teal-50 via-white to-slate-50 px-4 py-4 sm:px-6 sm:py-5">
        <div className="flex items-start gap-3">
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal-700/12 text-teal-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]"
            aria-hidden>
            <ScrollText className="h-5 w-5" strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold tracking-tight text-slate-900">Activity log</h2>
            <p className="mt-1 text-sm leading-relaxed text-slate-600">
              Chronological timeline of updates and touchpoints.
            </p>
          </div>
        </div>
      </div>

      <div className="relative p-4 sm:p-6">
      {canAddStandaloneNote ? (
        <div className="fixed bottom-8 right-8 z-50">
          <Button
            displayType="primary"
            onClick={() => {
              setShowAddLogModal(true);
            }}
            customStyle={{
              borderRadius: '50%',
              width: '52px',
              height: '52px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0 6px 20px rgba(15, 118, 110, 0.35)',
              background: 'linear-gradient(145deg, #0f766e, #115e59)'
            }}>
            <Plus size={24} color="white" />
          </Button>
        </div>
      ) : null}

      {Array.isArray(activityId) && activityId.length > 0 && (
        <div className={variant === 'split' ? 'w-full' : 'w-full max-w-3xl'}>
          {activityId.map((item, index) => (
            <LogCard
              key={index}
              data={item}
              isSubmitting={isSubmitting}
              onCommentSubmit={handleCommentSubmit}
            />
          ))}
        </div>
      )}

      {(!Array.isArray(activityId) || activityId.length === 0) && (
        <div className="flex justify-center py-10">
          <div className="flex max-w-md flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-gray-200 bg-gray-50/80 px-8 py-12 text-center">
            <img
              src={NoDataFoundImg}
              alt="No activity yet"
              className="mx-auto w-[45%] max-w-[200px] object-contain opacity-90"
            />
            <h2 className="text-lg font-semibold text-gray-800">No logs yet</h2>
            <p className="text-sm leading-relaxed text-gray-600">
              When this lead has activity, it will appear here in order.
              {canAddStandaloneNote ? ' Use the button to add a note without updating follow-up.' : ''}
            </p>
          </div>
        </div>
      )}

      </div>

      {/* Add Log Modal */}
      {showAddLogModal && canAddStandaloneNote && (
        <Modal
          isDirty
          confirmOnClose
          width={50}
          name="Add Log"
          onClose={() => {
            setShowAddLogModal(false);
            setLogDescription('');
            setUploadedFile(null);
          }}>
          <div className="flex flex-col space-y-4">
            {/* Log Description Field */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Log Description <span className="text-red-500">*</span>
              </label>
              <textarea
                placeholder="Enter log description here..."
                value={logDescription}
                onChange={(e) => setLogDescription(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* File Upload */}
            {/* <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attachment (Optional)
              </label>
              <div className="flex items-center space-x-2">
                <label
                  htmlFor="log-file-upload"
                  className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  <input
                    id="log-file-upload"
                    type="file"
                    className="sr-only"
                    onChange={handleFileUpload}
                    accept="image/*,.pdf,.doc,.docx"
                    disabled={uploading}
                  />
                  {uploading ? 'Uploading...' : 'Choose File'}
                </label>
                {uploadedFile && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <span className="truncate max-w-xs">{uploadedFile.name}</span>
                    <button
                      onClick={() => setUploadedFile(null)}
                      className="text-red-500 hover:text-red-700">
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div> */}

            {/* Action Buttons */}
            <div className="w-full flex justify-end items-center space-x-4 mt-6 pt-4 border-t">
              <Button
                type="reset"
                onClick={() => {
                  setShowAddLogModal(false);
                  setLogDescription('');
                  setUploadedFile(null);
                }}
                displayType="secondary">
                Cancel
              </Button>
              <Button disabled={isSubmitting} type="button" onClick={handleAddLog}>
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Logs;